import React, { useRef, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./Header.module.scss";
import { additionlInfoModalActions } from "@/entities/additionInfoModal";
import mixpanel from "@/shared/lib/mixpanel";
import { useMe, useCurrentOrganization } from "@/Hooks";
import { Tooltip } from "@mui/material";
import { useGetVideosQuery } from "@/features/auth/api/authAPI";

interface HeaderProps {
  data: {
    icon: string;
    name: string;
    shortname: string;
    slug?: string;
  };
  appId: number;
}

const Header: React.FC<HeaderProps> = ({ data, appId }) => {
  const dispatch = useDispatch();
  const { me } = useMe();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const currentOrganization = useCurrentOrganization();
  const location = useLocation();
  const navigate = useNavigate();

  // Get current query params
  const queryParams = new URLSearchParams(location.search);
  const currentVideoId = queryParams.get("av");

  // Fetch videos for this app
  const { data: videos, isLoading: isLoadingVideos } = useGetVideosQuery(
    { appId },
    { skip: !appId },
  );

  // Set selected version from URL or default to first video in the list
  const [selectedVideoId, setSelectedVideoId] = useState<number | null>(
    currentVideoId ? parseInt(currentVideoId) : null,
  );

  // Update selectedVideoId when videos are loaded or currentVideoId changes
  useEffect(() => {
    if (videos?.results?.length && !selectedVideoId) {
      setSelectedVideoId(videos.results[0].id);
    } else if (currentVideoId) {
      setSelectedVideoId(parseInt(currentVideoId));
    }
  }, [videos, currentVideoId]);

  // Get the selected video from the videos list
  const selectedVideo = videos?.results?.find(
    (video) => video.id === selectedVideoId,
  );

  const handleExpand = () => {
    mixpanel.track("Info clicked", {
      app_name: data.name,
      idApp: appId,
      UserEmail: me?.email || "",
      OrganizationID: currentOrganization?.uuid || "",
    });
    dispatch(additionlInfoModalActions.toggleItem());
  };

  const handleVersionSelect = (videoId: number) => {
    // Update URL with the selected video ID
    const newParams = new URLSearchParams(queryParams);
    newParams.set("av", videoId.toString());

    // Update URL without reloading the page
    navigate(
      {
        pathname: location.pathname,
        search: newParams.toString(),
      },
      { replace: true },
    );

    setSelectedVideoId(videoId);
    setIsDropdownOpen(false);
  };

  const iconTimerSaveAppRef = useRef<NodeJS.Timeout | null>(null);
  const iconTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (iconTimerSaveAppRef.current)
        clearTimeout(iconTimerSaveAppRef.current);
      if (iconTimerRef.current) clearTimeout(iconTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  return (
    <>
      <div className={styles.root}>
        <div className={styles.containerLeft}>
          <div className={styles.containerTop}>
            <div className={styles.containerTitle}>
              <img
                className={styles.logo}
                src={
                  data!.icon === "" || !data!.icon
                    ? "/img/skeletonLogo.svg"
                    : data!.icon
                }
                alt={`${data!.name} logo icon`}
                loading="lazy"
                draggable={false}
              />
              <div className={styles.title}>
                <h1 title={data!.name}>{data!.name}</h1>
                <p title={data!.shortname}>{data!.shortname}</p>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.containerRight}>
          {videos?.results?.length > 1 && !isLoadingVideos && (
            <div
              className={styles.multipuleVersionContainer}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <img src="/img/MultipuleVersion.svg" alt="multipuleVersion" />
              <p>{selectedVideo?.label}</p>
              <img src="/img/blackIcon/DropDown.svg" alt="arrowDown" />

              {isDropdownOpen && (
                <div className={styles.multipuleVersionDropdown}>
                  {videos.results.map((video) => (
                    <p
                      key={video.id}
                      className={`${styles.multipuleVersionDropdownItem} ${selectedVideoId === video.id ? styles.active : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVersionSelect(video.id);
                      }}
                    >
                      {video.label}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
          <Tooltip
            title="See info"
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  background:
                    "linear-gradient(90deg, rgb(35 97 217), rgb(94 36 217) 100%)",
                  color: "white",
                  fontSize: "0.8",
                  padding: "8px 12px",
                  borderRadius: "24px",
                  margin: "2px 2px 3px !important",
                },
              },
            }}
          >
            <div
              className={styles.containerExpand}
              onClick={() => handleExpand()}
            >
              <img
                className={styles.iconExpand}
                src="/img/blackIcon/Info.svg"
                alt="img-close"
                loading="lazy"
              />
            </div>
          </Tooltip>
        </div>
      </div>
    </>
  );
};

export default Header;
